import { createURLDescriptor, joinPaths } from './URLTools';
import { SimpleSubscription, createOrPassthroughObserver } from './Observable';
import { Status } from './Resolver';
import { SegmentType } from './Segments';
import { isRouteMapSteady } from './Maps';
import { createRoute } from './Route';
import { matchMappingAgainstPathname } from './Mapping';
import { HTTPMethod } from './HTTPMethod';

var RouteMapObservable =
/** @class */
function () {
  function RouteMapObservable(url, rootContext, pages, rootMapping, resolver, router, options) {
    var _this = this;

    this.handleUnsubscribe = function (observer) {
      var index = _this.observers.indexOf(observer);

      if (index !== -1) {
        _this.observers.splice(index, 1);
      }
    };

    this.handleChange = function () {
      _this.refresh();

      var isSteady = isRouteMapSteady(_this.cachedRouteMap);

      for (var i = 0; i < _this.observers.length; i++) {
        var observer = _this.observers[i];
        observer.next(_this.cachedRouteMap);

        if (isSteady && observer.complete) {
          observer.complete();
        }
      }

      if (isSteady) {
        _this.resolver.unlisten(_this.handleChange);

        delete _this.rootContext;
        delete _this.mapItems;
        delete _this.router;
        delete _this.observers;
        delete _this.resolver;
      }
    };

    this.refresh = function () {
      var allResolvableIds = [];
      var i = 0;

      while (i < _this.mapItems.length) {
        var item = _this.mapItems[i];
        var pathname = item.pathname;

        var _a = item.matcher.getResult(),
            segment = _a.segment,
            resolutionIds = _a.resolutionIds;

        var lastSegment = segment.lastRemainingSegment || segment;
        var cachedLastSegment = item.lastSegmentCache;
        item.segmentCache = segment;
        item.lastSegmentCache = lastSegment; // If an item in the map cannot be found, throws an error, or is
        // no longer referenced by other items, then remove it from the
        // map.
        //
        // Note that later items in the map should always be "from" earlier
        // items, so if an earlier item is removed, its referenced items
        // will still be removed.

        if (lastSegment.status === Status.Error || _this.options.predicate && !_this.options.predicate(lastSegment)) {
          _this.removeFromQueue(item);

          continue;
        } // If a redirect has been added or changed `to` location,
        // then add the location to the map.


        if (_this.options.followRedirects && lastSegment.type === SegmentType.Redirect && lastSegment.status === Status.Ready && lastSegment.to && (!cachedLastSegment || cachedLastSegment.type !== SegmentType.Redirect || cachedLastSegment.status !== Status.Ready || !cachedLastSegment.to || cachedLastSegment.to !== lastSegment.to)) {
          _this.addToQueue(lastSegment.to, item.depth + 1, pathname, item.order);
        }

        if (lastSegment.type === SegmentType.Switch && lastSegment.status === Status.Ready && (!cachedLastSegment || cachedLastSegment.type !== SegmentType.Switch || cachedLastSegment.status === Status.Busy)) {
          var patterns = lastSegment.switch.patterns;

          for (var j = 0; j < patterns.length; j++) {
            _this.addToQueue(joinPaths(pathname, patterns[j]), item.depth + 1, pathname, item.order + '.' + j);
          }
        }

        if (resolutionIds) {
          allResolvableIds = allResolvableIds.concat(resolutionIds);
        }

        i++;
      } // This will replace any existing listener and its associated resolvables


      _this.resolver.listen(_this.handleChange, allResolvableIds);

      var routeMap = [];

      for (var i_1 = 0; i_1 < _this.mapItems.length; i_1++) {
        var item = _this.mapItems[i_1];
        var lastSegment = item.lastSegmentCache;

        if (lastSegment.type !== SegmentType.Switch && lastSegment.status !== Status.Error) {
          routeMap.push([joinPaths(item.pathname, '/'), createRoute(createURLDescriptor(item.pathname, {
            ensureTrailingSlash: false
          }), item.segmentCache), item.order]);
        }
      }

      routeMap.sort(function (x, y) {
        return x[2] < y[2] ? -1 : x[2] > y[2] ? 1 : 0;
      });
      _this.cachedRouteMap = {};

      for (var i_2 = 0; i_2 < routeMap.length; i_2++) {
        var _b = routeMap[i_2],
            pathname = _b[0],
            route = _b[1];
        _this.cachedRouteMap[pathname] = route;
      }
    };

    this.observers = [];
    this.mapItems = [];
    this.resolver = resolver;
    this.router = router;
    this.rootContext = rootContext;
    this.pages = pages;
    this.rootMapping = rootMapping;
    this.options = options;
    this.seenPathnames = new Set();
    var pathname = url.pathname; // A final '/' always indicates a Page or Redirect, and it
    // doesn't really make sense to build a map of a single page/redirect.

    if (pathname.substr(-1) === '/') {
      pathname = pathname.substr(0, pathname.length - 1);
    }

    this.addToQueue(pathname, 0);
  }

  RouteMapObservable.prototype.subscribe = function (onNextOrObserver, onError, onComplete) {
    if (!this.resolver) {
      throw new Error("Can't subscribe to an already-complete RoutingObservable.");
    }

    var observer = createOrPassthroughObserver(onNextOrObserver, onError, onComplete);
    this.observers.push(observer);
    var subscription = new SimpleSubscription(this.handleUnsubscribe, observer);

    if (this.observers.length === 1) {
      this.handleChange();
    }

    return subscription;
  };

  RouteMapObservable.prototype.removeFromQueue = function (item) {
    var i = this.mapItems.indexOf(item);

    if (i !== -1) {
      this.mapItems.splice(i, 1);
    }
  };

  RouteMapObservable.prototype.addToQueue = function (pathname, depth, fromPathname, order) {
    if (order === void 0) {
      order = '0';
    }

    if (this.seenPathnames.has(pathname)) {
      return;
    }

    if (!this.options.maxDepth || depth <= this.options.maxDepth) {
      this.seenPathnames.add(pathname);
      var url = createURLDescriptor(pathname, {
        ensureTrailingSlash: false
      });
      var rootEnv = {
        context: this.rootContext,
        method: HTTPMethod.Get,
        params: {},
        pathname: '',
        query: url.query,
        search: url.search,
        router: this.router,
        unmatchedPathnamePart: url.pathname
      };
      var matchEnv = matchMappingAgainstPathname(rootEnv, this.rootMapping, false);

      if (matchEnv) {
        this.mapItems.push({
          url: url,
          fromPathname: fromPathname,
          depth: depth,
          pathname: pathname,
          order: order,
          matcher: new this.pages({
            appendFinalSlash: false,
            env: matchEnv,
            resolver: this.resolver
          })
        });
      }
    }
  };

  return RouteMapObservable;
}();

export { RouteMapObservable };